import React, { useEffect, useRef, useState } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import { IconDownChevron } from "icons";

import Button from "./Button";

import styles from "./Dropdown.module.scss";

function Dropdown({
  className,
  children,
  label,
  buttonType,
  alignRight,
  alignCenter,
  alignStretch,
  height,
  width,
  noIcon,
  outlineOnFocus,
  hasBorder,
  onClick,
  disabled,
  renderFn,
}) {
  // State and setter for matched value
  const container = useRef();
  const [visible, setVisible] = useState(false);

  const handleOnClick = e => {
    if (onClick) {
      onClick(e);
    }

    if (disabled) {
      return;
    }

    toggleDropdown(e);
  };

  const toggleDropdown = e => {
    e.stopPropagation();
    setVisible(!visible);
  };

  useEffect(() => {
    const handleClick = e => {
      if (container.current.contains(e.target)) return;
      setVisible(false);
    };

    // Add click listener when component is mounted
    if (visible) {
      window.addEventListener("mousedown", handleClick);
    } else {
      window.removeEventListener("mousedown", handleClick);
    }

    // Clean up listener when when unmounted
    return () => {
      window.removeEventListener("mousedown", handleClick);
    };
  }, [setVisible, visible]);

  return (
    <div
      className={classnames(
        styles.dropdown_container,
        styles[`height_${height}`],
        styles[`width_${width}`],
        hasBorder && styles.border,
        visible && outlineOnFocus && styles["dropdown--focused"],
        className,
      )}
      ref={container}
    >
      <Button onClick={handleOnClick} type={buttonType}>
        {label}
        {!noIcon && (
          <div className={styles.dropdown_indicator}>
            <IconDownChevron size="sm" color="deep-green" />
          </div>
        )}
      </Button>
      <div
        className={classnames(
          styles.dropdown_list,
          alignRight && styles.align_right,
          alignCenter && styles.align_center,
          alignStretch && styles.align_stretch,
          visible && styles.visible,
        )}
      >
        {children}
        {renderFn && renderFn(visible)}
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  alignCenter: PropTypes.bool,
  alignRight: PropTypes.bool,
  alignStretch: PropTypes.bool,
  buttonType: PropTypes.string,
  children: PropTypes.element,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hasBorder: PropTypes.bool,
  height: PropTypes.oneOf(["sm", ""]),
  label: PropTypes.element,
  noIcon: PropTypes.bool,
  onClick: PropTypes.func,
  outlineOnFocus: PropTypes.bool,
  width: PropTypes.oneOf(["fit-content", ""]),
  renderFn: PropTypes.func,
};

Dropdown.defaultProps = {
  alignCenter: false,
  alignRight: false,
  buttonType: "dropdown",
  children: null,
  disabled: false,
  hasBorder: false,
  height: "",
  label: "",
  noIcon: false,
  onClick: null,
  outlineOnFocus: true,
  width: "",
};

export default Dropdown;
